// React
import React, {
  useCallback,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Site } from 'interfaces';

// Mantine
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_SortingState,
  MRT_TableOptions,
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
} from 'mantine-react-table';
import { Center, Flex, Text } from '@mantine/core';

// Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useTranslation, useNotification } from '@refinedev/core';

// Sentry
import * as Sentry from '@sentry/react';

// Components
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { TableProgressBar } from 'components/common/TableProgressBar';
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { InternalRefreshButton } from 'components/common/InternalRefreshButton';
import { UploadButton } from 'components/common/UploadButton';
import { UploadViewerModal } from './components/UploadViewerModal';
import { UploadCreateModal } from './components/UploadCreateModal';

// Columns
import { UploadColumns } from 'columns/Upload';

// Components
import {
  applyUploadStatusGroup,
  getActiveUploadStatusGroup,
  UPLOAD_DEFAULT_STATUS_GROUP,
  UploadStatusGroup,
  UploadStatusGroupCounts,
} from './components/uploadStatus';
import { UploadStatusQuickFilters } from './components/UploadStatusQuickFilters';

// Hooks
import { useAssignableUploadUsers } from './components/useAssignableUploadUsers';
import { useGetIntakeItem, useUpdateIntakeItem } from 'api/resource';
import { useCanAssign, useCanModify, useCanUpload, useIsProvider } from 'hooks';
import { useCustomColumnSorting } from 'hooks/useCustomColumnSorting';
import { useCustomColumnVisibility } from 'hooks/useCustomColumnVisibility';
import { useCurrentUserStore } from 'store/UserStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useQueryClient } from '@tanstack/react-query';
import { IntakeItem } from '@billing/validation';

interface UploadManagementProps {
  items: IntakeItem[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  errorMessage: string;
  pagination: {
    pageIndex: number;
    pageSize: number;
    globalFilter: string;
    columnFilters: MRT_ColumnFiltersState;
    sorting: MRT_SortingState;
  };
  statusGroupCounts: UploadStatusGroupCounts;
  setPagination: Dispatch<
    SetStateAction<{
      pageIndex: number;
      pageSize: number;
      globalFilter: string;
      columnFilters: MRT_ColumnFiltersState;
      sorting: MRT_SortingState;
    }>
  >;
  sites?: Site[];
}

export const UploadManagement: React.FC<UploadManagementProps> = ({
  items,
  total,
  isLoading,
  isError,
  isFetching,
  errorMessage,
  pagination,
  statusGroupCounts,
  setPagination,
  sites,
}) => {
  const { translate } = useTranslation();
  const title = translate('pages:uploads.pageTitle', 'Upload Management');
  useDocumentTitle(
    translate('pages:uploads.documentTitle', 'Uploads | QBilling')
  );
  const { id: editingID } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { users, isLoading: isLoadingUsers } = useAssignableUploadUsers();
  const updateIntakeItem = useUpdateIntakeItem();

  const { id: currentUserID } = useCurrentUserStore();
  const { canAssign } = useCanAssign('uploads');
  const { canModify } = useCanModify('uploads');
  const { canUpload } = useCanUpload('uploads');
  const isProvider = useIsProvider();
  const { providerSiteSelection } = useCurrentSiteStore();
  const isAllSites = providerSiteSelection === 'all';
  const canCreateUploadForScope = canUpload && !(isProvider && isAllSites);
  const { open: openNotification } = useNotification();

  const [pendingAssignments, setPendingAssignments] = useState<
    Record<string, string | null>
  >({});
  const [savingAssignments, setSavingAssignments] = useState<Set<string>>(
    new Set()
  );
  const [pendingStatuses, setPendingStatuses] = useState<
    Record<string, string | null>
  >({});
  const [savingStatuses, setSavingStatuses] = useState<Set<string>>(new Set());
  const [viewIntakeOpen, setViewIntakeOpen] = useState(false);
  const [createUploadOpen, setCreateUploadOpen] = useState(false);
  const [selectedIntakeItem, setSelectedIntakeItem] =
    useState<IntakeItem | null>(null);
  const unresolvedEditingIDRef = useRef<string | null>(null);
  const intakeItemLoadErrorEditingIDRef = useRef<string | null>(null);

  const data = items ?? [];
  const itemFromPage = editingID
    ? data.find((item) => item.id === editingID) || null
    : null;
  const intakeItemIDToFetch = editingID || selectedIntakeItem?.id || '';
  const {
    data: fetchedIntakeItem,
    isFetched: hasFetchedIntakeItem,
    isFetching: isFetchingIntakeItem,
    isError: isIntakeItemError,
  } = useGetIntakeItem({
    id: intakeItemIDToFetch,
  });

  const handleViewIntake = (item: IntakeItem) => {
    setSelectedIntakeItem(item);
    setViewIntakeOpen(true);
    navigate(`/uploads/edit/${item.id}`);
  };
  const handleSetViewIntakeOpen = (newValue: boolean) => {
    setViewIntakeOpen(newValue);
    if (!newValue && editingID) {
      navigate('/uploads');
    }
  };

  useEffect(() => {
    if (!editingID) {
      unresolvedEditingIDRef.current = null;
      intakeItemLoadErrorEditingIDRef.current = null;
      setViewIntakeOpen(false);
      setSelectedIntakeItem(null);
      return;
    }

    const resolvedItem = fetchedIntakeItem || itemFromPage;
    if (resolvedItem) {
      unresolvedEditingIDRef.current = null;
      intakeItemLoadErrorEditingIDRef.current = null;
      setSelectedIntakeItem(resolvedItem);
      setViewIntakeOpen(true);
      return;
    }

    if (!hasFetchedIntakeItem || isFetchingIntakeItem) {
      setViewIntakeOpen(false);
      setSelectedIntakeItem(null);
      return;
    }

    // Query failed while resolving uuid. Do not redirect the user to /uploads.
    if (isIntakeItemError) {
      if (intakeItemLoadErrorEditingIDRef.current === editingID) return;
      intakeItemLoadErrorEditingIDRef.current = editingID;
      unresolvedEditingIDRef.current = null;
      setViewIntakeOpen(false);
      setSelectedIntakeItem(null);
      openNotification?.({
        message: translate(
          'pages:uploads.error.load.message',
          'Unable to load upload. Please try again.'
        ),
        type: 'error',
      });
      return;
    }

    // Query completed and explicitly returned no item -> invalid intake item ID.
    if (fetchedIntakeItem !== null) return;
    if (unresolvedEditingIDRef.current === editingID) return;
    unresolvedEditingIDRef.current = editingID;
    intakeItemLoadErrorEditingIDRef.current = null;
    setViewIntakeOpen(false);
    setSelectedIntakeItem(null);
    openNotification?.({
      message: translate(
        'pages:uploads.error.notFound.message',
        'Upload not found.'
      ),
      type: 'error',
    });
    navigate('/uploads');
  }, [
    editingID,
    fetchedIntakeItem,
    hasFetchedIntakeItem,
    isFetchingIntakeItem,
    isIntakeItemError,
    itemFromPage,
    navigate,
    openNotification,
    translate,
  ]);

  const handleAssign = async (
    item: IntakeItem,
    assigneeUserID: string | null
  ) => {
    setPendingAssignments((prev) => ({
      ...prev,
      [item.id]: assigneeUserID,
    }));
    setSavingAssignments((prev) => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });

    try {
      await updateIntakeItem.mutateAsync({
        id: item.id,
        assigneeUserID: assigneeUserID || null,
      });
    } catch (error) {
      Sentry.captureException(error as Error);
      openNotification?.({
        message: translate(
          'pages:uploads.error.assign.message',
          'Unable to Assign Upload'
        ),
        type: 'error',
      });
    } finally {
      setSavingAssignments((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setPendingAssignments((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, item.id)) return prev;
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  const handleStatusChange = async (item: IntakeItem, status: string) => {
    setPendingStatuses((prev) => ({
      ...prev,
      [item.id]: status,
    }));
    setSavingStatuses((prev) => {
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });

    try {
      await updateIntakeItem.mutateAsync({
        id: item.id,
        status,
      });
    } catch (error) {
      Sentry.captureException(error as Error);
      openNotification?.({
        message: translate(
          'pages:uploads.error.updateStatus.message',
          'Unable to Update Upload Status'
        ),
        type: 'error',
      });
    } finally {
      setSavingStatuses((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setPendingStatuses((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, item.id)) return prev;
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  const columns: MRT_ColumnDef<IntakeItem>[] = UploadColumns({
    assignableUsers: users,
    onAssign: handleAssign,
    pendingAssignments,
    savingAssignments,
    isLoadingUsers,
    currentUserID,
    canAssign,
    onStatusChange: handleStatusChange,
    pendingStatuses,
    savingStatuses,
    canModify,
    onViewIntake: handleViewIntake,
    sites,
  });

  const activeUploadStatusGroup = useMemo(
    () => getActiveUploadStatusGroup(pagination.columnFilters),
    [pagination.columnFilters]
  );

  const setUploadStatusGroup = useCallback(
    (group: UploadStatusGroup) => {
      setPagination((prev) => ({
        ...prev,
        columnFilters: applyUploadStatusGroup(prev.columnFilters, group),
        pageIndex: 0,
      }));
    },
    [setPagination]
  );

  const hasInitializedDefaultStatusGroupRef = useRef(false);
  useEffect(() => {
    if (hasInitializedDefaultStatusGroupRef.current) return;

    const hasStatusFilter = (pagination.columnFilters ?? []).some(
      (filter) => filter.id === 'status'
    );
    if (hasStatusFilter) {
      hasInitializedDefaultStatusGroupRef.current = true;
      return;
    }

    hasInitializedDefaultStatusGroupRef.current = true;
    setUploadStatusGroup(UPLOAD_DEFAULT_STATUS_GROUP);
  }, [pagination.columnFilters, setUploadStatusGroup]);

  const initialColumnVisibility = {
    actions: true,
    intakeNumber: true,
    provider: true,
    createdAt: true,
    linkedClaims: true,
    'claims.serviceStart': true,
    status: true,
    assignedUserID: true,
    siteID: false,
  };
  const { columnVisibility, onColumnVisibilityChange } =
    useCustomColumnVisibility({
      initialColumns: initialColumnVisibility,
      conditionalColumns: { siteID: isProvider && isAllSites },
    });
  const handleSortingChange = useCallback(
    (sorting: MRT_SortingState) => {
      setPagination((prev) => ({ ...prev, sorting, pageIndex: 0 }));
    },
    [setPagination]
  );
  const { sorting, onSortingChange } = useCustomColumnSorting({
    initialSorting: pagination.sorting,
    onSortingChange: handleSortingChange,
  });

  const tableOptions: MRT_TableOptions<IntakeItem> = {
    data,
    columns,
    initialState: {
      globalFilter: pagination.globalFilter,
      columnFilters: pagination.columnFilters,
    },
    state: {
      pagination,
      globalFilter: pagination.globalFilter,
      columnFilters: pagination.columnFilters,
      sorting,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading || isFetchingIntakeItem,
      showLoadingOverlay: isFetching || isLoading || isFetchingIntakeItem,
      columnVisibility,
    },
    getRowId: (row) => row.id,
    rowCount: total,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    enableGlobalFilter: true,
    renderTopToolbarCustomActions: () => (
      <Flex align="center" gap="sm" wrap="wrap">
        {canCreateUploadForScope ? (
          <UploadButton
            onClick={() => {
              setCreateUploadOpen(true);
            }}
          />
        ) : null}
        <UploadStatusQuickFilters
          activeGroup={activeUploadStatusGroup}
          onGroupChange={setUploadStatusGroup}
          statusGroupCounts={statusGroupCounts}
        />
      </Flex>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Flex align="center" gap="0.313rem">
        <InternalRefreshButton queryClient={queryClient} />
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
      </Flex>
    ),
    onPaginationChange: (updater) =>
      setPagination((prev) => {
        const next =
          typeof updater === 'function'
            ? updater({ pageIndex: prev.pageIndex, pageSize: prev.pageSize })
            : updater;
        return {
          ...prev,
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        };
      }),
    onSortingChange,
    onColumnVisibilityChange,
    onColumnFiltersChange: (updater) =>
      setPagination((prev) => {
        const nextFilters =
          typeof updater === 'function' ? updater(prev.columnFilters) : updater;
        return { ...prev, columnFilters: nextFilters, pageIndex: 0 };
      }),
    onGlobalFilterChange: (value) =>
      setPagination((prev) => ({
        ...prev,
        globalFilter: value || '',
        pageIndex: 0,
      })),
    mantineSearchTextInputProps: {
      placeholder: translate(
        'pages:uploads.table.search',
        'Search intake items'
      ),
    },
    renderEmptyRowsFallback: () => (
      <Center style={{ height: '100%', margin: '6%' }}>
        <Text c="dimmed">
          {translate('pages:uploads.table.noItems', 'No intake items found.')}
        </Text>
      </Center>
    ),
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: errorMessage });
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onDoubleClick: () => handleViewIntake(row.original),
      role: 'row',
      style: {
        cursor: 'pointer',
      },
    }),
    enableRowActions: true,
    enableColumnPinning: true,
    enableFullScreenToggle: false,
  };

  return (
    <>
      <UploadViewerModal
        intakeItem={selectedIntakeItem}
        modalOpen={viewIntakeOpen}
        setModalOpen={handleSetViewIntakeOpen}
      />
      <UploadCreateModal
        opened={createUploadOpen}
        onClose={() => setCreateUploadOpen(false)}
      />
      <BillingDataGridWrapper
        title={title}
        dataTestID="taskManagement"
        tableOptions={tableOptions}
        showAlertBanner={false}
      />
    </>
  );
};
